/*
 * Login V2 from Figma (83:5223): sign-in page with Google / Apple / email, then the email code page with
 * six digit boxes and an on-screen number pad. The hero art, Google G, Apple mark, back control and delete
 * icon are the Figma exports in ./figma, used as they are. Everything shown comes from the app: the email
 * typed, the real resend countdown, the server's answer, and the counts of data saved in this browser.
 */
import type { AuthResult } from "../cloud/auth";
import { esc } from "../domain/format";
import { topBar } from "./app";
import { toast } from "./shell";
import heroUrl from "./figma/login-hero.svg";
import googleUrl from "./figma/google-g.svg";
import appleUrl from "./figma/apple-mark.png";
import backUrl from "./figma/back.svg";
import deleteUrl from "./figma/delete.svg";

export type Provider = "google" | "apple";
/** what the sign-in screens need from an auth backend (Supabase in cloud mode, a stand-in on the review preview) */
export interface AuthPort {
  sendCode(email: string): Promise<AuthResult>;
  verifyCode(email: string, code: string): Promise<AuthResult>;
  oauth(provider: Provider): Promise<AuthResult>;
  /** a code check the person had already left (went back, or it timed out) succeeded after all: undo that sign-in */
  discardSignIn?(): void;
  /** toast after a code was sent */
  sentMessage: string;
}
let port: AuthPort | null = null;
export const setAuthPort = (p: AuthPort) => { port = p; };

export const COOLDOWN_MS = 60_000;
let cooldownMs = COOLDOWN_MS;
/** review preview tests only */
export const setCooldown = (ms: number) => { cooldownMs = ms; };
export const CODE_LEN = 6;
/** how long a send, check or provider request may take before the buttons come back for another try */
let authTimeoutMs = 20_000;
export const setAuthTimeout = (ms: number) => { authTimeoutMs = ms; };
export const TIMEOUT_MESSAGE = "เครือข่ายไม่ตอบสนอง ลองอีกครั้งได้เลย";

export const login = {
  step: "email" as "email" | "sent",
  email: "", error: "", info: "", cooldownUntil: 0,
  /** a code is being sent (first send or resend) */
  busy: false,
  /** digits typed on the code page */
  code: "", verifying: false, codeError: "",
  /** a note about the last check that is not the code's fault (timeout, offline): the code stays and can be sent again */
  codeNote: "",
  oauthBusy: "" as "" | Provider
};

/*
 * Every request remembers the "turn" it started in. Going back, changing the email, leaving the page or a timeout
 * starts a new turn, so an answer that arrives later can never change the screen or the email now shown.
 */
let turn = 0;
const newTurn = () => ++turn;

export function resetLogin() {
  newTurn();
  Object.assign(login, { step: "email", email: "", error: "", info: "", busy: false, code: "", verifying: false, codeError: "", codeNote: "", oauthBusy: "" });
}

/**
 * Runs one auth request with the timeout. `current` is false when the person had moved on before the answer came.
 * When an abandoned request succeeds later, `lateOk` runs (used to undo a sign-in nobody is waiting for).
 */
function request(p: Promise<AuthResult>, lateOk?: () => void): Promise<{ r: AuthResult; current: boolean }> {
  const my = turn;
  let done = false;
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      const current = my === turn;
      if (current) newTurn(); // the answer, if it ever comes, belongs to a finished turn
      resolve({ r: { ok: false, message: TIMEOUT_MESSAGE }, current });
    }, authTimeoutMs);
    p.catch((): AuthResult => ({ ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" })).then(r => {
      if (done) { if (r.ok) lateOk?.(); return; }
      done = true; clearTimeout(timer);
      const current = my === turn;
      if (!current && r.ok) lateOk?.();
      resolve({ r, current });
    });
  });
}

const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const secondsLeft = () => Math.max(0, Math.ceil((login.cooldownUntil - Date.now()) / 1000));
const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const spinner = `<span class="spinner" aria-hidden="true"></span>`;

/* ---------- sign-in page (Figma 83:922) ---------- */

function signInHTML(localCounts: string) {
  const sending = login.busy, anyBusy = login.busy || !!login.oauthBusy;
  return topBar() + `<section class="lg2 login-root" id="login-page">
    <div class="lg2-hero" aria-hidden="true"><img src="${heroUrl}" alt="" width="390" height="310" draggable="false"></div>
    <div class="lg2-body">
      <h2>เก็บลิงก์และโน้ต<br>เปิดง่ายในที่เดียว</h2>
      <p class="slogan">จัดหมวดด้วยสี ค้นหา คัดลอก และแชร์ได้ง่าย</p>
      ${login.info ? `<p class="info" id="lg-info">${esc(login.info)}</p>` : ""}
      <div class="social">
        <button class="btn-social google" data-do="lg2-oauth" data-provider="google" id="btn-google" ${anyBusy ? "disabled" : ""} ${login.oauthBusy === "google" ? "aria-busy=\"true\"" : ""}><img src="${googleUrl}" alt="" width="20" height="20">ดำเนินการต่อด้วย Google</button>
        <button class="btn-social apple" data-do="lg2-oauth" data-provider="apple" id="btn-apple" ${anyBusy ? "disabled" : ""} ${login.oauthBusy === "apple" ? "aria-busy=\"true\"" : ""}><span class="apple-mark" aria-hidden="true"><img src="${appleUrl}" alt=""></span>ดำเนินการต่อด้วย Apple</button>
      </div>
      <div class="divider">หรือใช้อีเมล</div>
      <div class="lg2-field"><label for="lg-email">อีเมล</label>
        <input class="lg2-input" id="lg-email" type="email" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" value="${esc(login.email)}" placeholder="you@example.com" ${sending ? "readonly" : ""}>
        <span class="err" id="lg-email-err" hidden>กรุณาใส่อีเมลให้ถูกต้อง</span></div>
      <p class="err" id="lg-err" role="alert" ${login.error ? "" : "hidden"}>${esc(login.error)}</p>
      <button class="btn-v2 ${sending ? "loading" : ""}" data-do="lg2-send" id="btn-send-code" ${anyBusy ? "disabled" : ""} ${sending ? "aria-busy=\"true\"" : ""}>${sending ? spinner : ""}ส่งรหัสเข้าสู่ระบบ</button>
      <p class="reassure">ระบบส่งรหัส 6 หลักมาที่อีเมล ไม่ต้องตั้งรหัสผ่าน<br>เก็บใน Cloud เปิดใช้ได้ทุกเครื่อง</p>
      ${localCounts ? `<p class="local-note" id="login-local-note">เบราว์เซอร์นี้มีข้อมูลในเครื่อง (${localCounts}) ยังไม่ได้อัปโหลดอัตโนมัติ หลังเข้าสู่ระบบเลือกย้ายเข้าบัญชีได้</p>` : ""}
    </div></section>`;
}

/* ---------- code page (Figma 83:4840 empty · 83:4904 partial · 83:4971 complete · 83:5040 verifying · 83:5112 error) ---------- */

type OtpState = "empty" | "partial" | "complete" | "verifying" | "error";
const otpState = (): OtpState =>
  login.verifying ? "verifying" : login.codeError ? "error" : !login.code ? "empty" : login.code.length < CODE_LEN ? "partial" : "complete";

/** the six boxes are drawing only; the input over them is what the system, autofill and screen readers use */
function cellsHTML() {
  const s = otpState();
  const cells = Array.from({ length: CODE_LEN }, (_, i) => {
    const d = login.code[i];
    const focus = s !== "verifying" && s !== "error" && i === login.code.length;
    return `<span class="otp-d ${focus ? "focus" : ""}">${d ? esc(d) : ""}</span>`;
  }).join("");
  return `<div class="otp-fields ${s === "error" ? "bad" : ""}" id="otp-fields" aria-hidden="true">${cells}</div>`;
}
function inputHTML() {
  return `<input class="otp-input" id="otp-input" type="text" inputmode="numeric" autocomplete="one-time-code" autocapitalize="off" spellcheck="false"
    aria-labelledby="otp-label" aria-describedby="otp-status" value="${esc(login.code)}">`;
}
function statusHTML() {
  const s = otpState();
  if (s === "error") return `<p class="bad" role="alert" id="otp-error">${esc(login.codeError)}</p><small>แก้ไขรหัสหรือขอรหัสใหม่ก่อนยืนยัน</small>`;
  if (s === "complete" && login.codeNote) return `<p class="bad" role="alert" id="otp-note">${esc(login.codeNote)}</p><small>รหัสที่กรอกยังอยู่ แตะยืนยันรหัสเพื่อลองอีกครั้ง</small>`;
  return `<p>${s === "complete" ? "ตรวจสอบรหัสแล้วแตะยืนยัน" : s === "verifying" ? "กำลังตรวจสอบรหัส…" : "กรอกรหัสจากอีเมลให้ครบ 6 หลัก"}</p>`;
}
function resendHTML() {
  const left = secondsLeft(), ready = !left && !login.busy && !login.verifying;
  return `<button type="button" data-do="otp-resend" id="btn-resend" ${ready ? "" : "disabled"}>ส่งรหัสอีกครั้ง</button><span id="otp-cooldown">${login.busy ? "กำลังส่ง…" : left ? `${mmss(left)} วินาที` : "พร้อมส่งรหัสใหม่"}</span>`;
}
function confirmHTML() {
  const s = otpState();
  return `<button class="btn-v2 ${s === "verifying" ? "loading" : ""}" data-do="otp-verify" id="btn-verify" ${s === "complete" ? "" : "disabled"} ${s === "verifying" ? "aria-busy=\"true\"" : ""}>${s === "verifying" ? spinner : ""}ยืนยันรหัส</button>`;
}

function codePageHTML() {
  const lock = login.verifying ? "disabled" : "";
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"].map(k => `<button type="button" data-do="otp-key" data-k="${k}" ${lock}>${k}</button>`).join("")
    + `<button type="button" class="txt" data-do="otp-paste" id="btn-paste" ${lock}>วางรหัส</button>`
    + `<button type="button" data-do="otp-key" data-k="0" ${lock}>0</button>`
    + `<button type="button" class="txt" data-do="otp-del" id="btn-del" aria-label="ลบตัวเลขล่าสุด" ${lock}><img src="${deleteUrl}" alt="" width="20" height="16">ลบ</button>`;
  return `<section class="login-root" id="otp-page">
    <nav class="otp-nav"><button class="otp-back" data-do="otp-back" id="btn-otp-back" aria-label="กลับไปแก้อีเมล"><img src="${backUrl}" alt="" width="44" height="44"></button><span class="wordmark">MEMOLINK</span></nav>
    <div class="otp">
      <h2>ยืนยันอีเมล</h2>
      <p class="explain">เราส่งรหัส 6 หลักไปที่อีเมล</p>
      <div class="otp-dest"><b id="otp-email">${esc(login.email)}</b><button type="button" data-do="otp-back" id="btn-change-email">เปลี่ยนอีเมล</button></div>
      <div class="otp-entry">
        <label class="otp-label" id="otp-label" for="otp-input">รหัสยืนยัน 6 หลัก</label>
        <div class="otp-box">${inputHTML()}${cellsHTML()}</div>
        <div class="otp-status" id="otp-status" aria-live="polite">${statusHTML()}</div>
        <div class="otp-resend ${secondsLeft() ? "" : "ready"}" id="otp-resend">${resendHTML()}</div>
      </div>
      <div class="numpad-wrap"><div class="numpad" role="group" aria-label="แป้นตัวเลขสำหรับกรอกรหัส">${keys}</div></div>
      <div id="otp-confirm">${confirmHTML()}</div>
      <p class="otp-help">ใช้รหัสครั้งเดียวจากอีเมลเพื่อเข้าสู่ระบบ<br>ไม่ต้องตั้งรหัสผ่าน</p>
    </div></section>`;
}

/** updates the code page in place, so focus stays where it is and nothing jumps */
function paintCode() {
  const page = document.getElementById("otp-page"); if (!page) return;
  const input = page.querySelector<HTMLInputElement>("#otp-input")!;
  if (input.value !== login.code) input.value = login.code;
  input.readOnly = login.verifying;
  if (login.codeError) input.setAttribute("aria-invalid", "true"); else input.removeAttribute("aria-invalid");
  page.querySelector("#otp-fields")!.outerHTML = cellsHTML();
  page.querySelector("#otp-status")!.innerHTML = statusHTML();
  paintResend();
  page.querySelector("#otp-confirm")!.innerHTML = confirmHTML();
  page.querySelectorAll<HTMLButtonElement>(".numpad button").forEach(b => b.disabled = login.verifying);
}
function paintResend() {
  const el = document.getElementById("otp-resend"); if (!el) return;
  el.innerHTML = resendHTML();
  el.classList.toggle("ready", !secondsLeft());
}

let ticker: ReturnType<typeof setInterval> | undefined;
/** Draws the sign-in or code page. `localCounts` is the real summary of data saved in this browser ("" when none). */
export function renderLogin($app: HTMLElement, localCounts: string) {
  clearInterval(ticker);
  if (login.step === "sent") {
    $app.innerHTML = codePageHTML();
    ticker = setInterval(() => { if (!document.getElementById("otp-page")) { clearInterval(ticker); return; } paintResend(); }, 1000);
  } else {
    $app.innerHTML = signInHTML(localCounts);
  }
}
let rerender: () => void = () => {};
export const onLoginChange = (fn: () => void) => { rerender = fn; };
const onCodePage = () => login.step === "sent" && !!document.getElementById("otp-page");

/* ---------- actions ---------- */

async function send(fromCodePage: boolean) {
  if (!port || login.busy || login.oauthBusy) return;
  const input = document.querySelector<HTMLInputElement>("#lg-email");
  const email = fromCodePage ? login.email : (input?.value ?? "").trim().toLowerCase();
  if (!emailOk(email)) { const e = document.querySelector<HTMLElement>("#lg-email-err"); if (e) e.hidden = false; input?.focus(); return; }
  const wait = secondsLeft();
  if (email === login.email && wait > 0) {
    if (fromCodePage) return;
    // same address again within the cooldown: the earlier code is still the one to use
    newTurn();
    Object.assign(login, { step: "sent", error: "", code: "", codeError: "", codeNote: "" }); rerender(); return;
  }
  if (!fromCodePage) newTurn(); // a new address: nothing from the previous one may land on the screen any more
  login.busy = true; login.error = ""; login.email = email;
  fromCodePage ? paintResend() : rerender();
  const { r, current } = await request(port.sendCode(email));
  if (!current) return; // the person went back or changed the email meanwhile
  login.busy = false;
  if (!r.ok) {
    if (fromCodePage) { if (onCodePage()) { login.codeError = login.verifying ? "" : r.message; paintCode(); if (login.verifying) toast(r.message); } }
    else { login.error = r.message; rerender(); }
    return;
  }
  login.cooldownUntil = Date.now() + cooldownMs;
  if (fromCodePage) {
    // a check may be running, or digits typed: keep them, only the countdown starts again
    if (onCodePage()) { paintResend(); toast("ส่งรหัสใหม่แล้ว ใช้รหัสจากอีเมลล่าสุด"); }
    return;
  }
  Object.assign(login, { step: "sent", error: "", info: "", code: "", codeError: "", codeNote: "" });
  rerender();
  toast(port.sentMessage);
}

function setCode(digits: string) {
  if (login.verifying) return;
  const next = digits.replace(/\D/g, "").slice(0, CODE_LEN);
  if (next === login.code) { paintCode(); return; }
  login.code = next; login.codeError = ""; login.codeNote = "";
  paintCode();
}
const type = (digits: string) => setCode(login.code + digits.replace(/\D/g, ""));
const del = () => { if (login.code) setCode(login.code.slice(0, -1)); };
async function paste() {
  let text = "";
  try { text = await navigator.clipboard.readText(); } catch { /* not allowed: fall back to the system paste */ }
  const digits = text.replace(/\D/g, "");
  if (digits.length < CODE_LEN) { toast("ไม่พบรหัส 6 หลักในคลิปบอร์ด คัดลอกรหัสจากอีเมลแล้วลองอีกครั้ง"); return; }
  setCode(digits);
}
async function verify() {
  if (!port || login.verifying || login.code.length < CODE_LEN) return;
  const p = port;
  login.verifying = true; login.codeError = ""; login.codeNote = ""; paintCode();
  const { r, current } = await request(p.verifyCode(login.email, login.code), () => p.discardSignIn?.());
  if (!current) return; // went back meanwhile: that screen already reset itself
  login.verifying = false;
  if (!r.ok) {
    if (r.message === TIMEOUT_MESSAGE || /เชื่อมต่อไม่ได้/.test(r.message)) login.codeNote = r.message; // not the code's fault: keep it, allow another try
    else login.codeError = r.message;
    paintCode(); return;
  }
  // signed in: the auth event opens the account and replaces this page
}
async function oauth(p: Provider) {
  if (!port || login.oauthBusy || login.busy) return;
  login.oauthBusy = p; login.error = "";
  rerender();
  const { r, current } = await request(port.oauth(p));
  if (!current) return;
  login.oauthBusy = "";
  if (!r.ok) { login.error = r.message; rerender(); }
  // ok: the browser is on its way to the provider
}
function back() {
  newTurn(); // a check or send still running is now abandoned
  Object.assign(login, { step: "email", code: "", codeError: "", codeNote: "", verifying: false, busy: false });
  rerender();
}

export function handleLoginAction(act: string, el: HTMLElement): boolean {
  switch (act) {
    case "lg2-send": void send(false); return true;
    case "lg2-oauth": void oauth(el.dataset.provider as Provider); return true;
    case "otp-back": back(); return true;
    case "otp-key": type(el.dataset.k ?? ""); return true;
    case "otp-del": del(); return true;
    case "otp-paste": void paste(); return true;
    case "otp-verify": void verify(); return true;
    case "otp-resend": void send(true); return true;
  }
  return false;
}

// back from Google / Apple without finishing: a page the browser restores from its back cache still shows the button busy
addEventListener("pageshow", e => { if (e.persisted && login.oauthBusy) { login.oauthBusy = ""; rerender(); } });

// the real input: typing, system autofill (one-time-code) and assistive tech all arrive here
document.addEventListener("input", e => {
  const t = e.target as HTMLInputElement;
  if (t.id !== "otp-input") return;
  if (login.verifying) { t.value = login.code; return; }
  setCode(t.value);
});
// physical keyboard and system paste work on the code page even when the input is not focused
document.addEventListener("keydown", e => {
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target as HTMLElement;
  if (t.id === "lg-email" && e.key === "Enter") { e.preventDefault(); void send(false); return; }
  if (!document.getElementById("otp-page") || document.querySelector("#layer .sheet")) return;
  if (t.id === "otp-input") { if (e.key === "Enter") { e.preventDefault(); void verify(); } return; } // the input edits itself
  if (/^\d$/.test(e.key)) { e.preventDefault(); type(e.key); }
  else if (e.key === "Backspace") { e.preventDefault(); del(); }
  else if (e.key === "Enter" && t.tagName !== "BUTTON") { e.preventDefault(); void verify(); }
});
document.addEventListener("paste", e => {
  if (!document.getElementById("otp-page")) return;
  const text = e.clipboardData?.getData("text") ?? "";
  if (!text) return;
  e.preventDefault(); setCode(text);
});
